/**
 * Run summary panel shown above the execution mode selector. Confirms
 * what's about to be executed so the user has one final visual review
 * before clicking Run / Create Schedule.
 *
 * Renders only when wizard mode is active (the legacy stacked-steps
 * layout already shows all configuration inline).
 */

import {
  CalendarIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/solid';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type { PipelineSummary } from './pipelineEditor/PipelineSummaryContext';

interface RunSummaryProps {
  capabilityTitle: string;
  targetCount: number;
  targetLabel: string;
  targetPreview?: string;
  mode: 'now' | 'schedule';
  scanMode?: string;
  goal?: string;
  /** Editor-broadcast pipeline summary (preset + projected cost). */
  pipelineSummary?: PipelineSummary | null;
}

const usd = (n: number) => `$${n < 10 ? n.toFixed(2) : Math.round(n)}`;

export function RunSummary({
  capabilityTitle,
  targetCount,
  targetLabel,
  targetPreview,
  mode,
  scanMode,
  goal,
  pipelineSummary,
}: RunSummaryProps) {
  const isNow = mode === 'now';
  const overBudget = !!pipelineSummary?.overBudget;
  return (
    <section
      aria-label="Run summary"
      className={cn(
        'flex flex-col gap-3 rounded-md border p-4',
        overBudget
          ? 'border-chariot-status-critical/60 bg-chariot-status-critical/5'
          : 'border-chariot-stroke bg-chariot-background-primary/40'
      )}
    >
      <header className="flex items-center gap-2">
        {overBudget ? (
          <ExclamationTriangleIcon className="text-chariot-status-critical size-4" />
        ) : (
          <CheckBadgeIcon className="text-chariot-brand-primary size-4" />
        )}
        <h4
          className={cn(
            'text-xs font-semibold tracking-wider uppercase',
            overBudget
              ? 'text-chariot-status-critical'
              : 'text-chariot-text-primary'
          )}
        >
          {overBudget ? 'Over budget — cannot run' : 'Ready to run'}
        </h4>
      </header>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-chariot-text-secondary">Capability</dt>
        <dd className="text-chariot-text-primary truncate font-medium">
          {capabilityTitle}
        </dd>

        <dt className="text-chariot-text-secondary">Targets</dt>
        <dd className="text-chariot-text-primary flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className="font-medium">
            {targetCount} {targetCount === 1 ? targetLabel : `${targetLabel}s`}
          </span>
          {targetPreview && (
            <span className="text-chariot-text-secondary truncate text-xs">
              {targetPreview}
            </span>
          )}
        </dd>

        <dt className="text-chariot-text-secondary">Execution</dt>
        <dd className="text-chariot-text-primary flex items-center gap-1.5">
          {isNow ? (
            <PlayCircleIcon className="text-chariot-brand-primary size-3.5" />
          ) : (
            <CalendarIcon className="text-chariot-brand-primary size-3.5" />
          )}
          {isNow ? 'Run immediately' : 'Recurring schedule'}
        </dd>

        {pipelineSummary && (
          <>
            <dt className="text-chariot-text-secondary">Pipeline</dt>
            <dd className="text-chariot-text-primary flex min-w-0 flex-wrap items-baseline gap-x-2">
              <span className="font-medium">
                {pipelineSummary.presetDisplayName}
              </span>
              {pipelineSummary.costIndicator && (
                <Badge
                  variant="secondary"
                  className="px-1.5 py-0 font-mono text-[10px] tracking-wider"
                >
                  {pipelineSummary.costIndicator}
                </Badge>
              )}
              {pipelineSummary.presetSummary && (
                <span className="text-chariot-text-secondary truncate text-xs">
                  {pipelineSummary.presetSummary}
                </span>
              )}
            </dd>

            <dt className="text-chariot-text-secondary">Projected cost</dt>
            <dd
              className={cn(
                'flex flex-wrap items-baseline gap-x-2',
                overBudget
                  ? 'text-chariot-status-critical'
                  : 'text-chariot-text-primary'
              )}
            >
              <span className="font-medium">
                {pipelineSummary.hasUnavailable &&
                pipelineSummary.projectedCost === 0
                  ? '— (calibration pending)'
                  : usd(pipelineSummary.projectedCost)}
              </span>
              <span className="text-chariot-text-secondary text-xs">
                of {usd(pipelineSummary.budget)} per-scan cap
              </span>
              {overBudget && (
                <span className="text-chariot-status-critical text-xs font-medium">
                  · scan will be blocked
                </span>
              )}
            </dd>
          </>
        )}

        {scanMode && scanMode !== 'auto' && (
          <>
            <dt className="text-chariot-text-secondary">Scan mode</dt>
            <dd className="text-chariot-text-primary capitalize">{scanMode}</dd>
          </>
        )}

        {goal && goal.trim() && (
          <>
            <dt className="text-chariot-text-secondary">Goal</dt>
            <dd className="text-chariot-text-primary line-clamp-2 text-xs italic">
              &ldquo;{goal}&rdquo;
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}
